import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, CircularProgress, Alert, Button, Dialog,
  DialogTitle, DialogContent, DialogActions, FormControlLabel, Switch,
  TextField, IconButton, Chip, Accordion, AccordionSummary, AccordionDetails,
  Autocomplete, InputAdornment
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Edit as EditIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import api from '../../services/api';

const TOOL_CATEGORIES = {
  therapeutic: { label: 'Therapeutic', color: 'primary' },
  safety: { label: 'Safety', color: 'error' },
  administrative: { label: 'Admin', color: 'secondary' },
  general: { label: 'General', color: 'default' }
};

const PersonaToolAssignments = ({ token }) => {
  const [personas, setPersonas] = useState([]);
  const [expandedPersona, setExpandedPersona] = useState(null);
  const [personaTools, setPersonaTools] = useState({});
  const [allTools, setAllTools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Edit dialog state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [currentAssignment, setCurrentAssignment] = useState(null);
  const [customInstructions, setCustomInstructions] = useState('');
  
  // Bulk assignment dialog state
  const [bulkAssignDialogOpen, setBulkAssignDialogOpen] = useState(false);
  const [sourcePersonaKey, setSourcePersonaKey] = useState('');
  const [targetPersonaKey, setTargetPersonaKey] = useState('');

  // Main list search filter
  const [searchQuery, setSearchQuery] = useState('');

  // Filter personas by name / key / description / personaPurpose. Case-insensitive,
  // matches any token (space-separated) — useful for queries like "youth caregiver".
  const filteredPersonas = React.useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return personas;
    const tokens = q.split(/\s+/);
    return personas.filter((p) => {
      const haystack = [
        p.name,
        p.key,
        p.description,
        p.personaPurpose,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return tokens.every((t) => haystack.includes(t));
    });
  }, [personas, searchQuery]);

  // Load all personas and tools on component mount
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [personasResponse, toolsResponse] = await Promise.all([
          api.get('/personas', token),
          api.get('/tools', token)
        ]);
        
        setPersonas(personasResponse.data || []);
        setAllTools(toolsResponse.data || []);
        setError('');
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to load personas and tools');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [token]);

  // Load persona tools when a persona is expanded
  const handlePersonaExpand = async (personaKey) => {
    if (expandedPersona === personaKey) {
      setExpandedPersona(null);
      return;
    }
    
    setExpandedPersona(personaKey);
    
    // Check if we've already loaded this persona's tools
    if (personaTools[personaKey]) {
      return;
    }
    
    try {
      setLoading(true);
      const response = await api.get(`/personas/${personaKey}/tools`, token);
      setPersonaTools(prev => ({
        ...prev,
        [personaKey]: response.data
      }));
    } catch (err) {
      console.error(`Error fetching tools for persona ${personaKey}:`, err);
      setError(`Failed to load tools for selected persona`);
    } finally {
      setLoading(false);
    }
  };
  
  // Toggle tool assignment
  const toggleToolAssignment = async (personaKey, toolId, currentlyEnabled) => {
    try {
      setLoading(true);
      
      // If the tool is not assigned yet, we need to assign it first
      if (!isToolAssigned(personaKey, toolId)) {
        await api.post(`/tools/${toolId}/assign/${personaKey}`, 
          { enabled: true, customInstructions: null }, 
          token
        );
        
        // Update local state to show the tool is now assigned
        updateToolAssignment(personaKey, toolId, true, null);
        
        setSuccess(`Tool assigned to "${personaKey}" successfully`);
        return;
      }
      
      // If the tool is already assigned, toggle its enabled status
      const assignmentOptions = {
        enabled: !currentlyEnabled,
        customInstructions: getCustomInstructions(personaKey, toolId)
      };
      
      await api.post(`/tools/${toolId}/assign/${personaKey}`, 
        assignmentOptions, 
        token
      );
      
      // Update local state to reflect the new enabled status
      updateToolAssignment(personaKey, toolId, !currentlyEnabled, assignmentOptions.customInstructions);
      
      setSuccess(`Tool ${currentlyEnabled ? 'disabled' : 'enabled'} for "${personaKey}" successfully`);
    } catch (err) {
      console.error('Error toggling tool assignment:', err);
      setError(err.response?.data?.msg || 'Failed to toggle tool assignment');
    } finally {
      setLoading(false);
    }
  };
  
  // Open edit dialog for custom instructions
  const openEditDialog = (personaKey, toolId) => {
    const tool = getToolById(toolId);
    const persona = personas.find(p => p.key === personaKey);
    const instructions = getCustomInstructions(personaKey, toolId) || '';
    
    setCurrentAssignment({
      personaKey,
      personaName: persona?.name || personaKey,
      toolId,
      toolName: tool?.name || 'Unknown Tool',
      enabled: isToolEnabled(personaKey, toolId)
    });
    
    setCustomInstructions(instructions);
    setEditDialogOpen(true);
  };
  
  // Save custom instructions
  const saveCustomInstructions = async () => {
    if (!currentAssignment) return;
    
    try {
      setLoading(true);
      
      const assignmentOptions = {
        enabled: currentAssignment.enabled,
        customInstructions: customInstructions.trim() || null
      };
      
      await api.post(`/tools/${currentAssignment.toolId}/assign/${currentAssignment.personaKey}`, 
        assignmentOptions, 
        token
      );
      
      // Update local state to reflect the new custom instructions
      updateToolAssignment(
        currentAssignment.personaKey, 
        currentAssignment.toolId, 
        currentAssignment.enabled,
        customInstructions.trim() || null
      );
      
      setSuccess('Custom instructions updated successfully');
      setEditDialogOpen(false);
    } catch (err) {
      console.error('Error updating custom instructions:', err);
      setError(err.response?.data?.msg || 'Failed to update custom instructions');
    } finally {
      setLoading(false);
    }
  };
  
  // Open bulk assignment dialog
  const openBulkAssignDialog = () => {
    setSourcePersonaKey(personas.length > 0 ? personas[0].key : '');
    setTargetPersonaKey('');
    setBulkAssignDialogOpen(true);
  };
  
  // Perform bulk assignment
  const performBulkAssignment = async () => {
    if (!sourcePersonaKey || !targetPersonaKey) {
      setError('Please select both source and target personas');
      return;
    }
    
    try {
      setLoading(true);
      
      // Get source persona with tools
      const sourcePersona = personaTools[sourcePersonaKey];
      if (!sourcePersona) {
        // Load source persona tools if not already loaded
        const response = await api.get(`/personas/${sourcePersonaKey}/tools`, token);
        const sourceWithTools = response.data;
        
        // Copy each tool assignment from source to target
        for (const assignment of sourceWithTools.toolAssignments || []) {
          if (assignment.toolId && typeof assignment.toolId === 'object') {
            await api.post(`/tools/${assignment.toolId._id}/assign/${targetPersonaKey}`, 
              { 
                enabled: assignment.enabled,
                customInstructions: assignment.customInstructions 
              }, 
              token
            );
          }
        }
      } else {
        // Copy each tool assignment from source to target
        for (const assignment of sourcePersona.toolAssignments || []) {
          if (assignment.toolId && typeof assignment.toolId === 'object') {
            await api.post(`/tools/${assignment.toolId._id}/assign/${targetPersonaKey}`, 
              { 
                enabled: assignment.enabled,
                customInstructions: assignment.customInstructions 
              }, 
              token
            );
          }
        }
      }
      
      // Clear the target persona's cached tools so they'll reload with new assignments
      setPersonaTools(prev => ({
        ...prev,
        [targetPersonaKey]: null
      }));
      
      setSuccess(`Tool assignments copied from "${sourcePersonaKey}" to "${targetPersonaKey}" successfully`);
      setBulkAssignDialogOpen(false);
      
      // If the target persona is currently expanded, reload its tools
      if (expandedPersona === targetPersonaKey) {
        const response = await api.get(`/personas/${targetPersonaKey}/tools`, token);
        setPersonaTools(prev => ({
          ...prev,
          [targetPersonaKey]: response.data
        }));
      }
    } catch (err) {
      console.error('Error performing bulk assignment:', err);
      setError(err.response?.data?.msg || 'Failed to copy tool assignments');
    } finally {
      setLoading(false);
    }
  };
  
  // Helper function to check if a tool is assigned to a persona
  const isToolAssigned = (personaKey, toolId) => {
    const persona = personaTools[personaKey];
    if (!persona) return false;
    
    return persona.toolAssignments?.some(
      assignment => assignment.toolId && 
      (assignment.toolId._id === toolId || assignment.toolId === toolId)
    );
  };
  
  // Helper function to check if a tool is enabled for a persona
  const isToolEnabled = (personaKey, toolId) => {
    const persona = personaTools[personaKey];
    if (!persona) return false;
    
    const assignment = persona.toolAssignments?.find(
      assignment => assignment.toolId && 
      (assignment.toolId._id === toolId || assignment.toolId === toolId)
    );
    
    return assignment?.enabled || false;
  };
  
  // Helper function to get custom instructions for a tool
  const getCustomInstructions = (personaKey, toolId) => {
    const persona = personaTools[personaKey];
    if (!persona) return null;
    
    const assignment = persona.toolAssignments?.find(
      assignment => assignment.toolId && 
      (assignment.toolId._id === toolId || assignment.toolId === toolId)
    );
    
    return assignment?.customInstructions || null;
  };
  
  // Helper function to get a tool by ID
  const getToolById = (toolId) => {
    return allTools.find(tool => tool._id === toolId);
  };
  
  // Helper function to update tool assignment in local state
  const updateToolAssignment = (personaKey, toolId, enabled, customInstructions) => {
    const persona = personaTools[personaKey];
    if (!persona) return;
    
    const updatedAssignments = [...persona.toolAssignments];
    const existingIndex = updatedAssignments.findIndex(
      assignment => assignment.toolId && 
      (assignment.toolId._id === toolId || assignment.toolId === toolId)
    );
    
    if (existingIndex >= 0) {
      // Update existing assignment
      updatedAssignments[existingIndex] = {
        ...updatedAssignments[existingIndex],
        enabled,
        customInstructions
      };
    } else {
      // Add new assignment
      const tool = getToolById(toolId);
      updatedAssignments.push({
        toolId: tool,
        enabled,
        customInstructions
      });
    }
    
    setPersonaTools(prev => ({
      ...prev,
      [personaKey]: {
        ...persona,
        toolAssignments: updatedAssignments
      }
    }));
  };
  
  // Render the edit dialog
  const renderEditDialog = () => {
    if (!currentAssignment) return null;
    
    return (
      <Dialog 
        open={editDialogOpen} 
        onClose={() => setEditDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Edit Custom Instructions
        </DialogTitle>
        <DialogContent>
          <Typography variant="subtitle1" sx={{ mb: 2 }}>
            {currentAssignment.toolName} for {currentAssignment.personaName}
          </Typography>
          
          <TextField
            label="Custom Instructions"
            multiline
            rows={8}
            fullWidth
            value={customInstructions}
            onChange={(e) => setCustomInstructions(e.target.value)}
            placeholder="Enter custom instructions for how this persona should use this tool. Leave empty to use the default instructions."
            sx={{ mb: 2 }}
          />
          
          <Typography variant="body2" color="text.secondary">
            These instructions override the default guidance for this specific persona.
            Use this to customize how a particular persona should use this tool.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>
            Cancel
          </Button>
          <Button 
            onClick={saveCustomInstructions}
            variant="contained"
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    );
  };
  
  // Render the bulk assignment dialog
  const renderBulkAssignDialog = () => {
    return (
      <Dialog 
        open={bulkAssignDialogOpen} 
        onClose={() => setBulkAssignDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Copy Tool Assignments
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 3 }}>
            Copy all tool assignments from one persona to another. This will overwrite any existing assignments on the target persona.
          </Typography>
          
          <Autocomplete
            options={personas}
            value={personas.find((p) => p.key === sourcePersonaKey) || null}
            onChange={(_, value) => setSourcePersonaKey(value ? value.key : '')}
            getOptionLabel={(p) => `${p.name} (${p.key})`}
            isOptionEqualToValue={(opt, val) => opt.key === val.key}
            filterOptions={(opts, { inputValue }) => {
              const q = inputValue.trim().toLowerCase();
              if (!q) return opts;
              const tokens = q.split(/\s+/);
              return opts.filter((p) => {
                const hay = [p.name, p.key, p.description, p.personaPurpose]
                  .filter(Boolean).join(' ').toLowerCase();
                return tokens.every((t) => hay.includes(t));
              });
            }}
            renderOption={(props, p) => (
              <li {...props} key={p.key}>
                <Box sx={{ display: 'flex', flexDirection: 'column', py: 0.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{p.name}</Typography>
                    {p.personaPurpose && (
                      <Chip label={p.personaPurpose} size="small" variant="outlined" sx={{ height: 18, fontSize: '0.65rem' }} />
                    )}
                  </Box>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontFamily: 'monospace' }}>{p.key}</Typography>
                  {p.description && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        mt: 0.25,
                      }}
                    >
                      {p.description}
                    </Typography>
                  )}
                </Box>
              </li>
            )}
            renderInput={(params) => (
              <TextField {...params} label="Source Persona" placeholder="Search by name, key, or description" />
            )}
            sx={{ mb: 3 }}
          />

          <Autocomplete
            options={personas.filter((p) => p.key !== sourcePersonaKey)}
            value={personas.find((p) => p.key === targetPersonaKey) || null}
            onChange={(_, value) => setTargetPersonaKey(value ? value.key : '')}
            getOptionLabel={(p) => `${p.name} (${p.key})`}
            isOptionEqualToValue={(opt, val) => opt.key === val.key}
            filterOptions={(opts, { inputValue }) => {
              const q = inputValue.trim().toLowerCase();
              if (!q) return opts;
              const tokens = q.split(/\s+/);
              return opts.filter((p) => {
                const hay = [p.name, p.key, p.description, p.personaPurpose]
                  .filter(Boolean).join(' ').toLowerCase();
                return tokens.every((t) => hay.includes(t));
              });
            }}
            renderOption={(props, p) => (
              <li {...props} key={p.key}>
                <Box sx={{ display: 'flex', flexDirection: 'column', py: 0.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{p.name}</Typography>
                    {p.personaPurpose && (
                      <Chip label={p.personaPurpose} size="small" variant="outlined" sx={{ height: 18, fontSize: '0.65rem' }} />
                    )}
                  </Box>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontFamily: 'monospace' }}>{p.key}</Typography>
                  {p.description && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        mt: 0.25,
                      }}
                    >
                      {p.description}
                    </Typography>
                  )}
                </Box>
              </li>
            )}
            renderInput={(params) => (
              <TextField {...params} label="Target Persona" placeholder="Search by name, key, or description" />
            )}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkAssignDialogOpen(false)}>
            Cancel
          </Button>
          <Button 
            onClick={performBulkAssignment}
            variant="contained"
            color="primary"
            disabled={loading || !sourcePersonaKey || !targetPersonaKey}
          >
            {loading ? <CircularProgress size={24} /> : 'Copy Assignments'}
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  // Main render function
  return (
    <div>
      {/* Notifications */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}
      
      {/* Header with actions */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5">Persona Tool Assignments</Typography>
        
        <Button 
          variant="contained" 
          color="primary"
          onClick={openBulkAssignDialog}
          disabled={loading || personas.length < 2}
        >
          Copy Assignments Between Personas
        </Button>
      </Box>
      
      {/* Loading indicator */}
      {loading && !expandedPersona && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Search filter for the persona list */}
      {personas.length > 0 && (
        <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
          <TextField
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search personas by name, key, description, or purpose"
            size="small"
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: searchQuery ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearchQuery('')} aria-label="clear search">
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />
          <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
            {filteredPersonas.length} of {personas.length}
          </Typography>
        </Box>
      )}

      {/* Personas list */}
      {filteredPersonas.length > 0 ? (
        filteredPersonas.map((persona) => (
          <Accordion
            key={persona.key}
            expanded={expandedPersona === persona.key}
            onChange={() => handlePersonaExpand(persona.key)}
            sx={{ mb: 2 }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', width: '100%', gap: 2 }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="h6">{persona.name}</Typography>
                  {persona.description && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        mt: 0.25,
                      }}
                    >
                      {persona.description}
                    </Typography>
                  )}
                </Box>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ ml: 2, whiteSpace: 'nowrap', flexShrink: 0 }}
                >
                  {persona.key}

                  {personaTools[persona.key] && (
                    <Chip
                      label={`${personaTools[persona.key].toolAssignments.filter(a => a.enabled).length} active tools`}
                      size="small"
                      sx={{ ml: 2 }}
                      color="primary"
                    />
                  )}
                </Typography>
              </Box>
            </AccordionSummary>
            
            <AccordionDetails>
              {loading && expandedPersona === persona.key && !personaTools[persona.key] ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <>
                  <Typography variant="subtitle2" sx={{ mb: 2 }}>
                    Tool Assignments
                  </Typography>
                  
                  {personaTools[persona.key] ? (
                    <TableContainer component={Paper}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Tool Name</TableCell>
                            <TableCell>Category</TableCell>
                            <TableCell>Description</TableCell>
                            <TableCell align="center">Enabled</TableCell>
                            <TableCell>Custom Instructions</TableCell>
                            <TableCell>Actions</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {allTools.map((tool) => {
                            const isAssigned = isToolAssigned(persona.key, tool._id);
                            const isEnabled = isToolEnabled(persona.key, tool._id);
                            const hasCustomInstructions = !!getCustomInstructions(persona.key, tool._id);
                            
                            return (
                              <TableRow 
                                key={`${persona.key}-${tool._id}`}
                                sx={{ 
                                  opacity: isAssigned ? 1 : 0.5,
                                  '&:hover': { opacity: 1 }
                                }}
                              >
                                <TableCell>{tool.name}</TableCell>
                                <TableCell>
                                  <Chip 
                                    label={TOOL_CATEGORIES[tool.category]?.label || tool.category} 
                                    color={TOOL_CATEGORIES[tool.category]?.color || 'default'}
                                    size="small"
                                  />
                                </TableCell>
                                <TableCell>{tool.description}</TableCell>
                                <TableCell align="center">
                                  <Switch
                                    checked={isEnabled}
                                    onChange={() => toggleToolAssignment(persona.key, tool._id, isEnabled)}
                                    color="primary"
                                    size="small"
                                  />
                                </TableCell>
                                <TableCell>
                                  {hasCustomInstructions ? (
                                    <Chip 
                                      label="Custom" 
                                      color="info"
                                      size="small"
                                      icon={<CheckIcon />}
                                    />
                                  ) : (
                                    <Chip 
                                      label="Default" 
                                      variant="outlined"
                                      size="small"
                                      sx={{ opacity: 0.7 }}
                                    />
                                  )}
                                </TableCell>
                                <TableCell>
                                  <IconButton
                                    size="small"
                                    onClick={() => openEditDialog(persona.key, tool._id)}
                                    title="Edit custom instructions"
                                  >
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No tools data available for this persona.
                    </Typography>
                  )}
                </>
              )}
            </AccordionDetails>
          </Accordion>
        ))
      ) : personas.length === 0 ? (
        <Typography variant="body1" sx={{ textAlign: 'center', my: 4 }}>
          No personas found. Create therapist personas first to manage tool assignments.
        </Typography>
      ) : (
        <Typography variant="body1" sx={{ textAlign: 'center', my: 4 }} color="text.secondary">
          No personas match "{searchQuery}". Try a different term or clear the search.
        </Typography>
      )}
      
      {/* Render dialogs */}
      {renderEditDialog()}
      {renderBulkAssignDialog()}
    </div>
  );
};

export default PersonaToolAssignments;
